import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { useDispatch } from "react-redux";

import AlertContainer from "../AlertContainer";
import PropertyDropdown from "../common/PropertyDropdown";
import ValueInput from "../common/ValueInput";
import ErrorText from "../common/ErrorText";
import AlertNameInput from "../common/AlertNameInput";
import LastTradedPriceInput from "../common/LastTradedPriceInput";
import NoteInput from "../common/NoteInput";
import { AlertData } from "../../../hooks/api/get/getAlerts";
import { Alerts } from "../AlertTools";
import useUpdateAlert from "../../../hooks/api/put/useUpdateAlert";
import { toggleAlertsListModel } from "../../../slices/alertSlices";
import {
  Conditions,
  propertyToStringForEdit,
} from "../../../helper/alert/condition";
import { deriveCondition } from "../alert.helper";
import { Quotes, ScripSearchedData } from "../../../models/symbolData";
import { zodResolver } from "@hookform/resolvers/zod";
import BrokerApi from "../../../chart-brokerapi/broker";
import { typeMap } from "../common/SearchResultsDropdown";
import nextId from "react-id-generator";
import { DataFeedInstance } from "../../../chart-datafeed/udf-compatible-datafeed";
import MarketSegmentDetails from "../common/MarketSegmentDetails";
import Skeleton from "../common/RectangleSkeltion";
import { alertFormSchema } from "./alertFormSchema.model";

interface CreateOrEditFormProps {
  handleAlertsTab: (alerts: Alerts) => void;
  probs: Omit<AlertData, "status">;
}

type AlertFormData = z.infer<typeof alertFormSchema>;

const EditAlertForm: React.FC<CreateOrEditFormProps> = ({
  handleAlertsTab,
  probs,
}) => {
  const [data, setData] = useState<ScripSearchedData[] | null>(null);
  const serchType = "All";
  const [selectedToken, setSelectedToken] = useState<number | null>(null);
  const [selectedMarketSegmentId, setSelectedMarketSegmentId] = useState<
    number | null
  >(null);
  const [isnIsIndex, setIsnIsIndex] = useState<boolean>(false);

  const hasBeenMountedRef = useRef(false);
  const isUpdatingFromValueInr = useRef(false);
  const isUpdatingFromLastTradedPrice = useRef(false);

  const [selectedMarketSegment, setSelectedMarketSegment] = useState<
    Quotes["v"] | null
  >(null);


  const memoizedSelectedMarketSegment = useMemo(() => {
    if (!selectedMarketSegment) return null;

    if (hasBeenMountedRef.current) {
      const payload = {
        volume: selectedMarketSegment.volume,
        lp: selectedMarketSegment.lp,
        averageTradePrice: selectedMarketSegment.average_trade_price || 0,
      };
      return payload;
    }

    return null;
  }, [hasBeenMountedRef.current]);

  const dispatch = useDispatch();
  const { mutate } = useUpdateAlert();

  const propertyOptions = useMemo(() => {
    return isnIsIndex
      ? [
        { value: "", label: propertyToStringForEdit("") },
        { value: "LTP", label: propertyToStringForEdit("LTP") },
      ]
      : [
        { value: "", label: propertyToStringForEdit("") },
        { value: "LTP", label: propertyToStringForEdit("LTP") },
        { value: "AVGPRICE", label: propertyToStringForEdit("AVGPRICE") },
        { value: "VOLUME", label: propertyToStringForEdit("VOLUME") },
      ];
  }, [isnIsIndex]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<AlertFormData>({
    resolver: zodResolver(alertFormSchema),
    mode: "onChange",
    defaultValues: {
      alertName: "",
      property: "",
      valueInr: "",
      lastTradedPrice: "",
      note: "",
      volume: undefined,
      ltp: undefined,
      averageTradePrice: undefined,
    },
  });

  useEffect(() => {
    if (memoizedSelectedMarketSegment) {
      let newValue: string;
      if (watch("property") === "VOLUME") {
        newValue = memoizedSelectedMarketSegment.volume?.toFixed(2);
      } else if (watch("property") === "AVGPRICE") {
        newValue = `${memoizedSelectedMarketSegment.averageTradePrice}`;
      } else {
        newValue = `${memoizedSelectedMarketSegment.lp}`;
      }

      setValue("valueInr", newValue);
      setValue("lastTradedPrice", "0");
    }
  }, [watch("property"), setValue]);

  useEffect(() => {
    if (isUpdatingFromLastTradedPrice.current) {
      isUpdatingFromLastTradedPrice.current = false;
      return;
    }

    const property = watch("property");
    const valueInr = watch("valueInr");

    if (memoizedSelectedMarketSegment && valueInr) {
      let intialValue: number;
      if (property === "VOLUME") {
        intialValue = memoizedSelectedMarketSegment.volume;
      } else if (property === "AVGPRICE") {
        intialValue = memoizedSelectedMarketSegment.averageTradePrice;
      } else {
        intialValue = memoizedSelectedMarketSegment.lp;
      }

      const percentageChange = ((parseFloat(valueInr) - intialValue) / intialValue) * 100;

      isUpdatingFromValueInr.current = true;
      setValue("lastTradedPrice", percentageChange.toFixed(2));
    }
  }, [watch("valueInr"), watch("property"), memoizedSelectedMarketSegment, setValue]);

  useEffect(() => {
    if (isUpdatingFromValueInr.current) {
      isUpdatingFromValueInr.current = false;
      return;
    }

    const property = watch("property");
    const lastTradedPrice = watch("lastTradedPrice");

    if (memoizedSelectedMarketSegment && lastTradedPrice) {
      let baseValue: number;
      if (property === "VOLUME") {
        baseValue = memoizedSelectedMarketSegment.volume;
      } else if (property === "AVGPRICE") {
        baseValue = memoizedSelectedMarketSegment.averageTradePrice;
      } else {
        baseValue = memoizedSelectedMarketSegment.lp;
      }

      const newValue = baseValue * (1 + parseFloat(lastTradedPrice) / 100);

      isUpdatingFromLastTradedPrice.current = true;
      setValue("valueInr", newValue.toFixed(2));
    }
  }, [watch("lastTradedPrice"), watch("property"), memoizedSelectedMarketSegment, setValue]);

  useEffect(() => {
    if (!probs.stock_data?.symbol) return;
    DataFeedInstance?.searchSymbols(
      probs.stock_data.symbol,
      "All Exchanges",
      typeMap[serchType.toUpperCase()],
      (res: ScripSearchedData[]) => {
        setData(res);
      }
    );
  }, [probs.stock_data?.symbol]);

  useEffect(() => {
    trigger(["valueInr"]);
  }, [watch("property"), watch('lastTradedPrice'), watch('valueInr'), trigger]);

  useEffect(() => {
    const match = data?.find(
      (item) => item._source.nToken === Number(probs.token)
    );

    if (match?._source.nIsIndex) {
      setIsnIsIndex(!!match._source.nIsIndex);
    }
  }, [data, probs.token]);

  useEffect(() => {
    let intialValue: number | undefined;
    if (probs.property === "VOLUME") {
      intialValue = memoizedSelectedMarketSegment?.volume;
    } else if (probs.property === "AVGPRICE") {
      intialValue = memoizedSelectedMarketSegment?.averageTradePrice;
    } else {
      intialValue = memoizedSelectedMarketSegment?.lp;
    }
    const chnagedValue = probs.triggerValue;

    if (probs) {
      let alertPercentValueChange: number = 0;
      if (intialValue) {
        alertPercentValueChange =
          ((chnagedValue - intialValue) / intialValue) * 100;
      }

      reset({
        searchQuery: probs.stock_data?.symbol || "",
        alertName: probs.name || "",
        property: probs.property || "",
        valueInr: probs.triggerValue?.toString() || "",
        lastTradedPrice: alertPercentValueChange.toFixed(2),
        note: probs.note || "",
        ltp: memoizedSelectedMarketSegment?.lp,
        volume: memoizedSelectedMarketSegment?.volume,
        averageTradePrice: memoizedSelectedMarketSegment?.averageTradePrice,
      });

      setSelectedToken(probs.token);
      setSelectedMarketSegmentId(probs.marketSegmentId);
    } else {
      console.warn("Missing probs:", { probs });
    }
  }, [probs, reset, memoizedSelectedMarketSegment]);

  

  const onSubmit = useCallback(
    async (data: AlertFormData) => {
      if (!selectedMarketSegmentId || !selectedToken) return;

      const payload = {
        id: probs.id,
        basketId: null,
        condition: deriveCondition(
          data.property,
          parseFloat(data.valueInr),
          data.ltp || 0,
          data.averageTradePrice || 0,
        ),
        marketSegmentId: selectedMarketSegmentId,
        name: data.alertName.trim(),
        note: data.note?.trim() || "",
        property: data.property.trim(),
        triggerValue: parseFloat(data.valueInr),
        token: selectedToken,
      };

      try {
        await mutate(payload);
        BrokerApi.instance?._host.showNotification(
          "Alert Updated Successfully",
          `${data.alertName} has been updated successfully`,
          1
        );
        handleAlertsTab("all-alerts");
        dispatch(toggleAlertsListModel(false));
      } catch (error: any) {
        BrokerApi.instance?._host.showNotification(
          error?.message || "Something Went Wrong",
          `${data.alertName} has not been updated successfully`,
          1
        );
      }
    },
    [
      selectedMarketSegmentId,
      selectedToken,
      probs.id,
      mutate,
      handleAlertsTab,
      dispatch,
    ]
  );

  useEffect(() => {
    let listenerGuid: string | undefined;

    if (selectedToken && selectedMarketSegmentId) {
      listenerGuid = nextId();

      const handleQuotesUpdate = (quotes: Quotes[]) => {
        const selectedSegment = quotes[0].v;

        if (hasBeenMountedRef.current) {
          if (
            JSON.stringify(selectedSegment) !==
            JSON.stringify(selectedMarketSegment)
          ) {
            setSelectedMarketSegment(selectedSegment);
            setValue('ltp', selectedSegment.lp);
            setValue('volume', selectedSegment.volume);
            setValue('averageTradePrice', selectedSegment.average_trade_price || 0);
          }
        } else {
          setSelectedMarketSegment(selectedSegment);
          setValue('ltp', selectedSegment.lp);
          setValue('volume', selectedSegment.volume);
          setValue('averageTradePrice', selectedSegment.average_trade_price || 0);
          hasBeenMountedRef.current = true;
        }
      };

      DataFeedInstance?.subcscribeQuotesFromTokens(
        [{ market_segment_id: selectedMarketSegmentId, token: selectedToken }],
        handleQuotesUpdate,
        listenerGuid
      );
    }

    // Cleanup function to unsubscribe from the data feed
    return () => {
      if (listenerGuid) {
        DataFeedInstance?.unsubscribeQuotes(listenerGuid);
      }
    };
  }, [selectedToken, selectedMarketSegmentId]);

  if (!probs.stock_data?.symbol) return null;

  return (
    <>
      <AlertContainer>
        <div className="px-5 py-4">
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="searchQuery"
              control={control}
              render={({ field }) => (
                <div className="position-relative w-100">
                  {data ? (
                    <MarketSegmentDetails
                      data={data}
                      isEditable={false}
                      formData={{ searchQuery: probs.stock_data.symbol }}
                      selectedMarketSegment={selectedMarketSegment!}
                      reSetInput={() => field.onChange("")}
                    />
                  ) : (
                    <Skeleton
                      className="bg-gray"
                      style={{ width: "100%", height: isnIsIndex ? 60 : 103 }}
                    />
                  )}
                </div>
              )}
            />

            {errors.searchQuery && (
              <ErrorText message={errors.searchQuery.message || ""} />
            )}

            <Controller
              name="alertName"
              control={control}
              render={({ field }) => (
                <AlertNameInput
                  label="Alert Name"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  disabled={false}
                  error={errors.alertName?.message}
                />
              )}
            />

            {errors.alertName && (
              <ErrorText message={errors.alertName.message || ""} />
            )}

            <Controller
              name="property"
              control={control}
              render={({ field }) => (
                <PropertyDropdown
                  label="Property"
                  value={field.value}
                  propertyOptions={propertyOptions}
                  onChange={(e) => field.onChange(e.target.value)}
                  disabled={!watch("alertName")}
                />
              )}
            />

            <div className="d-flex gap-2 mt-4">
              <div className="flex-grow-1">
                <Controller
                  name="valueInr"
                  control={control}
                  render={({ field }) => (
                    <ValueInput
                      property={watch("property") as Conditions}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={!watch("property")}
                      error={errors.valueInr?.message}
                    />
                  )}
                />
                {errors.valueInr && (
                  <ErrorText message={errors.valueInr.message || ""} />
                )}
              </div>
              <div className="flex-grow-1">
                <Controller
                  name="lastTradedPrice"
                  control={control}
                  render={({ field }) => (
                    <LastTradedPriceInput
                      property={watch("property") as Conditions}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={!watch("property")}
                      error={errors.lastTradedPrice?.message}
                    />
                  )}
                />
              </div>
            </div>

            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <NoteInput
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  onClear={() => field.onChange("")}
                  disabled={!watch("property")}
                />
              )}
            />
          </Form>
        </div>
      </AlertContainer>

      <div
        className="px-5 alerts-create-edit-background d-flex justify-content-end gap-3 py-2"
        style={{ height: 60 }}
      >
        <button
          type="button"
          className="btn-alert secondary"
          onClick={() => handleAlertsTab("all-alerts")}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-alert primary"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          style={{
            cursor: !isValid ? "not-allowed" : "pointer",
            opacity: !isValid ? 0.7 : 1,
          }}
        >
          Update
        </button>
      </div>
    </>
  );
};

export default EditAlertForm;
